// Phase 85.A.185 — Register UX rewrite:
//   • Trường SĐT chuyển xuống DƯỚI CÙNG (sau Password + Confirm Password).
//   • Gộp "Gửi OTP" + "Đăng ký" → chỉ còn 1 nút duy nhất "Gửi OTP".
//   • Sau khi user nhập đủ 6 số OTP, tự động submit
//     `/auth/register-with-otp` → BE verify OTP + tạo user + set cookies
//     trong 1 request → FE redirect về `/`. Giảm 1 click cho user.
//   • Validate đầy đủ TẤT CẢ các field TRƯỚC khi cho phép bấm "Gửi OTP"
//     (đỡ phí OTP cho user thiếu thông tin).
//   • SĐT đã có tài khoản → BE chặn ngay ở `/otp/send` (HTTP 409), FE
//     hiển thị warning + link "Đăng nhập".
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  User,
  Lock,
  Phone,
  IdentificationCard,
  ShieldCheck,
  ArrowsClockwise,
  GenderMale,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { formatApiErrorDetail } from "../lib/authApi";
import Logo from "../components/common/Logo";
import BackButton from "../components/common/BackButton";
import GoogleSignInButton from "../components/auth/GoogleSignInButton";
import AppleSignInButton from "../components/auth/AppleSignInButton";
import LegalDocModal from "../components/legal/LegalDocModal";

function normalizePhone(raw) {
  return (raw || "").replace(/\D/g, "");
}
function looksLikeVietnamesePhone(p) {
  const n = normalizePhone(p);
  return n.length >= 9 && n.length <= 11;
}

export default function Register() {
  const { registerWithOtp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteId = searchParams.get("invite");

  const [form, setForm] = useState({
    username: "",
    full_name: "",
    gender: "",
    password: "",
    password_confirm: "",
    phone: "",
  });
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpDebug, setOtpDebug] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [busy, setBusy] = useState(false); // dùng cho auto-submit register
  const [err, setErr] = useState("");
  // v16.60.15 (Feb 2026) — Apple 1.2 (UGC precautions): EULA must be
  // explicitly agreed before signup. Bắt buộc user tick "Đồng ý Điều
  // khoản + Cam kết cộng đồng" trước khi bật nút Gửi OTP.
  const [agreeTos, setAgreeTos] = useState(false);
  // v16.60.19 — Bug1: mở Terms/Guidelines trong modal (không rời trang →
  // không mất dữ liệu form đã điền).
  const [legalDoc, setLegalDoc] = useState(null);

  // Phase 83y.39 — Live duplicate availability check (username + phone).
  const [usernameAvail, setUsernameAvail] = useState({ status: "idle" });
  const [phoneAvail, setPhoneAvail] = useState({ status: "idle" });

  const checkAvailability = async (field, value) => {
    if (!value || value.trim().length < 3) return { status: "idle" };
    try {
      const r = await api.get("/auth/check-availability", { params: { field, value } });
      if (r.data?.available) return { status: "ok" };
      return { status: "taken", reason: r.data?.reason || "Đã tồn tại" };
    } catch {
      return { status: "idle" };
    }
  };

  // Cooldown tick.
  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const t = setInterval(() => setOtpCooldown((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(t);
  }, [otpCooldown]);

  // Phase 85.A.177 — Track register intent (admin tab "Chờ đăng ký").
  useEffect(() => {
    const phoneN = normalizePhone(form.phone);
    if (phoneN.length < 9) return undefined;
    const t = setTimeout(() => {
      api.post("/auth/register-intent", {
        phone: phoneN,
        full_name: form.full_name?.trim() || null,
        username: form.username?.trim() || null,
      }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [form.phone, form.full_name, form.username]);

  // Phase 84.M.83 — Username regex check.
  const usernameValid = /^[a-z0-9_]{3,30}$/.test(form.username);
  const usernameInvalidReason = form.username.length === 0
    ? null
    : !/^[a-z0-9_]*$/.test(form.username)
      ? "Chỉ được dùng chữ thường (a-z), số (0-9) và dấu gạch dưới (_)."
      : form.username.length < 3
        ? "Tối thiểu 3 ký tự."
        : form.username.length > 30
          ? "Tối đa 30 ký tự."
          : null;

  const phoneOk = looksLikeVietnamesePhone(form.phone);
  const passwordsMatch = form.password.length > 0 && form.password === form.password_confirm;
  // v16.60.14 (Feb 2026) — Apple 5.1.1(v): gender KHÔNG còn bắt buộc khi
  // đăng ký. VĐV có thể cập nhật sau. genderOk giữ lại (=true) cho các
  // check khác không cần refactor.
  const genderOk = true;
  const fullNameOk = form.full_name.trim().length > 0;
  const passwordOk = form.password.length >= 6;

  // Sẵn sàng gửi OTP: tất cả field hợp lệ, username & phone không trùng.
  const canSendOtp = useMemo(
    () =>
      usernameValid &&
      usernameAvail.status !== "taken" &&
      fullNameOk &&
      genderOk &&
      passwordOk &&
      passwordsMatch &&
      phoneOk &&
      phoneAvail.status !== "taken" &&
      agreeTos,
    [usernameValid, usernameAvail.status, fullNameOk, genderOk, passwordOk, passwordsMatch, phoneOk, phoneAvail.status, agreeTos],
  );

  // Lý do tại sao chưa enable nút "Gửi OTP" (helper text dưới nút).
  const sendOtpDisabledReason = useMemo(() => {
    if (!usernameValid) return "Hãy nhập tên đăng nhập hợp lệ (a-z, 0-9, _).";
    if (usernameAvail.status === "taken") return "Tên đăng nhập đã tồn tại.";
    if (!fullNameOk) return "Hãy nhập họ và tên.";
    if (!passwordOk) return "Mật khẩu tối thiểu 6 ký tự.";
    if (!passwordsMatch) return "Hai mật khẩu chưa khớp.";
    if (!phoneOk) return "Hãy nhập số điện thoại hợp lệ.";
    if (phoneAvail.status === "taken") return "SĐT này đã có tài khoản.";
    if (!agreeTos) return "Bạn cần đồng ý Điều khoản sử dụng để tiếp tục.";
    return null;
  }, [usernameValid, usernameAvail.status, fullNameOk, genderOk, passwordOk, passwordsMatch, phoneOk, phoneAvail.status, agreeTos]);

  const handleSendOtp = async () => {
    if (!canSendOtp || otpSending || otpCooldown > 0) return;
    setOtpSending(true);
    setErr("");
    try {
      const r = await api.post("/auth/otp/send", { phone: normalizePhone(form.phone) });
      setOtpSent(true);
      setOtpCooldown(60);
      if (r.data?.debug_code) {
        setOtpDebug(r.data.debug_code);
        toast.info(`OTP (chế độ test): ${r.data.debug_code}`, { duration: 8000 });
      } else {
        toast.success("OTP đã gửi qua Zalo ZNS. Vui lòng kiểm tra Zalo.");
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Không gửi được OTP";
      setErr(msg);
      toast.error(msg);
      // Nếu BE trả 409 → SĐT đã có tài khoản → cập nhật phoneAvail để
      // hiển thị link Đăng nhập.
      if (e.response?.status === 409) {
        setPhoneAvail({ status: "taken", reason: msg });
      }
    } finally {
      setOtpSending(false);
    }
  };

  // Auto-submit khi user gõ đủ 6 số OTP (giảm 1 click — không cần bấm
  // "Xác thực" nữa). User vẫn có thể bấm thủ công bằng nút "Xác thực &
  // tạo tài khoản" bên dưới.
  const handleSubmitOtp = async (otpValue = otp) => {
    if (busy || !otpSent || (otpValue || "").length < 4) return;
    setBusy(true);
    setErr("");
    try {
      const me = await registerWithOtp({
        username: form.username.trim().toLowerCase(),
        phone: normalizePhone(form.phone),
        otp: (otpValue || "").trim(),
        password: form.password,
        full_name: form.full_name.trim(),
        // v16.60.19 — Bug2: KHÔNG gửi gender rỗng (BE pattern ^(male|female)$
        // reject "" → 422 "string should match pattern").
        gender: form.gender || undefined,
      });
      toast.success(`Chào mừng ${me.full_name || me.username}!`);
      if (inviteId) {
        navigate(`/invite/${inviteId}`, { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (ex) {
      const msg = formatApiErrorDetail(ex.response?.data?.detail) || "OTP không đúng hoặc đã hết hạn";
      setErr(msg);
      toast.error(msg);
      // Cho user thử lại OTP — KHÔNG clear input để giữ những số đã gõ.
    } finally {
      setBusy(false);
    }
  };

  // Trigger auto-submit khi otp đủ 6 digits (debounce ngắn 250ms).
  useEffect(() => {
    if (otp.length !== 6 || !otpSent || busy) return undefined;
    const t = setTimeout(() => {
      handleSubmitOtp(otp);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, otpSent]);

  const onFormSubmit = (e) => {
    // Ngăn submit form mặc định (Enter key). Form thực sự "submit"
    // diễn ra trong handleSubmitOtp (khi OTP đủ 6 số).
    e.preventDefault();
    if (!otpSent) {
      handleSendOtp();
    } else {
      handleSubmitOtp();
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F14] text-white flex items-center justify-center px-5 relative overflow-hidden">
      <BackButton testId="register-back" />
      <LegalDocModal doc={legalDoc} onClose={() => setLegalDoc(null)} />
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#FF8A00]/8 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#FF8A00]/8 blur-3xl" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center mb-3" style={{ filter: "drop-shadow(0 0 18px rgba(255,138,0,0.35))" }}>
            <Logo variant="wordmark" size={56} priority alt="PickLeague" testid="register-logo" />
          </div>
        </div>

        <form
          onSubmit={onFormSubmit}
          className="bg-[#0D1420] border border-white/10 rounded-2xl p-8 shadow-2xl"
          data-testid="register-form"
        >
          <h1 className="font-heading text-2xl font-black tracking-tight mb-1">Đăng ký tài khoản</h1>
          <p className="text-white/50 text-sm mb-6">Tạo tài khoản vận động viên mới</p>

          {err && (
            <div
              className="mb-4 px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm"
              data-testid="register-error"
            >
              {err}
            </div>
          )}

          {/* 1. Username */}
          <Field icon={User} label="Tên đăng nhập">
            <input
              type="text"
              value={form.username}
              onChange={(e) => {
                const cleaned = (e.target.value || "")
                  .toLowerCase()
                  .replace(/[^a-z0-9_]/g, "")
                  .slice(0, 30);
                setForm({ ...form, username: cleaned });
                setUsernameAvail({ status: "idle" });
              }}
              onBlur={async () => {
                if (!usernameValid) return;
                setUsernameAvail({ status: "checking" });
                setUsernameAvail(await checkAvailability("username", form.username));
              }}
              required
              minLength={3}
              maxLength={30}
              pattern="^[a-z0-9_]{3,30}$"
              autoComplete="username"
              placeholder="Nhập tên đăng nhập của bạn"
              className={inputClass}
              data-testid="register-username"
              disabled={otpSent}
            />
          </Field>
          {usernameInvalidReason && (
            <div className="-mt-2 mb-2 flex items-center gap-1.5 text-[11px] text-amber-300" data-testid="register-username-format-warning">
              <span>⚠️</span>
              <span>{usernameInvalidReason}</span>
            </div>
          )}
          {usernameAvail.status === "taken" && (
            <div className="-mt-2 mb-2 flex items-center gap-1.5 text-[11px] text-red-400" data-testid="register-username-warning">
              <span>⚠️</span>
              <span>{usernameAvail.reason}</span>
              <Link to="/login" className="ml-auto text-[#FF8A00] font-bold hover:underline">Đăng nhập</Link>
            </div>
          )}

          {/* 2. Full name */}
          <Field icon={IdentificationCard}>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required
              placeholder="Họ và tên"
              className={inputClass}
              data-testid="register-full_name"
              disabled={otpSent}
            />
          </Field>

          {/* v16.60.14 (Feb 2026) — Bỏ block Giới tính khỏi màn đăng ký để
              tuân thủ Apple App Review Guideline 5.1.1(v). Rating mặc định =
              2.55 (mức Newbie tối thiểu). VĐV bổ sung giới tính sau ở
              trang Cá nhân; khi vào đăng ký giải mà chưa có giới tính +
              tỉnh thành → toast yêu cầu cập nhật. */}

          {/* 4. Password */}
          <Field icon={Lock}>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              placeholder="Mật khẩu (≥ 6 ký tự)"
              autoComplete="new-password"
              className={inputClass}
              data-testid="register-password"
              disabled={otpSent}
            />
          </Field>

          {/* 5. Confirm password */}
          <div>
            <Field icon={Lock}>
              <input
                type="password"
                value={form.password_confirm}
                onChange={(e) => setForm({ ...form, password_confirm: e.target.value })}
                required
                placeholder="Nhập lại mật khẩu"
                autoComplete="new-password"
                className={inputClass}
                data-testid="register-password-confirm"
                disabled={otpSent}
              />
            </Field>
            {form.password_confirm.length > 0 && (
              <div
                className={`text-[10.5px] mt-1 ml-1 flex items-center gap-1 ${
                  passwordsMatch ? "text-emerald-400" : "text-rose-400"
                }`}
                data-testid="register-password-match-hint"
              >
                {passwordsMatch ? "✓ Mật khẩu khớp" : "✗ Hai mật khẩu chưa giống nhau"}
              </div>
            )}
          </div>

          {/* 6. Phone — last field, with inline OTP-send button */}
          <div className="mb-2">
            <Field icon={Phone}>
              <input
                type="tel"
                inputMode="numeric"
                value={form.phone}
                onChange={(e) => {
                  setForm({ ...form, phone: e.target.value });
                  setPhoneAvail({ status: "idle" });
                  if (otpSent) {
                    // user thay đổi SĐT sau khi đã gửi OTP → reset flow.
                    setOtpSent(false);
                    setOtp("");
                  }
                }}
                onBlur={async () => {
                  if (!looksLikeVietnamesePhone(form.phone)) return;
                  setPhoneAvail({ status: "checking" });
                  setPhoneAvail(await checkAvailability("phone", form.phone));
                }}
                required
                placeholder="Số điện thoại"
                className={inputClass}
                data-testid="register-phone"
                disabled={otpSent && otpCooldown > 0}
              />
            </Field>
            {phoneAvail.status === "taken" && (
              <div className="-mt-2 mb-2 flex items-center gap-1.5 text-[11px] text-red-400" data-testid="register-phone-warning">
                <span>⚠️</span>
                <span>{phoneAvail.reason}</span>
                <Link to="/login" className="ml-auto text-[#FF8A00] font-bold hover:underline">Đăng nhập</Link>
              </div>
            )}
          </div>

          {/* v17.39 (Feb 2026) — Gender chọn ngay khi đăng ký (không bắt
              buộc — Apple 5.1.1 compliance). Pattern giống profile modal:
              Nam / Nữ / Không muốn tiết lộ. Nếu VĐV chọn Nữ → BE sẽ dùng
              default rating 2.05 (logic hiện có ở AthleteRegistrationPage /
              backend user seed rating). */}
          <div className="-mt-1" data-testid="register-gender-group">
            <div className="text-[11px] font-bold text-white/70 mb-1.5 uppercase tracking-wide">Giới tính (không bắt buộc)</div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "male", label: "Nam", wide: false },
                { id: "female", label: "Nữ", wide: false },
                { id: "prefer_not_to_say", label: "Không muốn tiết lộ", wide: true },
              ].map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setForm({ ...form, gender: g.id })}
                  className={`h-10 rounded-xl text-[12px] font-bold transition ${g.wide ? "col-span-2" : ""} ${
                    form.gender === g.id
                      ? "bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white shadow-[0_0_12px_rgba(255,138,0,0.35)]"
                      : "bg-white/5 border border-white/10 text-white/85 hover:bg-white/8"
                  }`}
                  data-testid={`register-gender-${g.id}`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {/* v16.60.15 (Feb 2026) — Apple 1.2: EULA + Community guidelines agreement. */}
          <label
            className="mt-2 flex items-start gap-2 cursor-pointer text-[11.5px] leading-snug text-white/70"
            data-testid="register-eula-agree-label"
          >
            <input
              type="checkbox"
              checked={agreeTos}
              onChange={(e) => setAgreeTos(e.target.checked)}
              disabled={otpSent}
              data-testid="register-eula-agree"
              className="mt-0.5 h-4 w-4 rounded border-white/25 bg-white/5 text-[#FF8A00] focus:ring-[#FF8A00] shrink-0"
            />
            <span>
              Tôi đồng ý với{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setLegalDoc("terms");
                }}
                className="text-[#FF8A00] underline font-bold"
                data-testid="register-terms-link"
              >
                Điều khoản sử dụng
              </button>{" "}
              và{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setLegalDoc("guidelines");
                }}
                className="text-[#FF8A00] underline font-bold"
                data-testid="register-guidelines-link"
              >
                Cam kết cộng đồng
              </button>
              . PickLeague áp dụng chính sách <b className="text-white/90">không khoan nhượng</b> với
              nội dung xúc phạm và hành vi lạm dụng.
            </span>
          </label>

          {/* 7. Single "Gửi OTP" action button (merged with submit) */}
          <button
            type="button"
            onClick={handleSendOtp}
            disabled={!canSendOtp || otpSending || (otpSent && otpCooldown > 0) || busy}
            data-testid="register-otp-send"
            className={`w-full mt-2 inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] hover:from-[#FF9F1C] hover:to-[#FFB300] text-white disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm rounded-lg transition shadow-[0_0_18px_rgba(255,138,0,0.35)] ${
              canSendOtp && !otpSending && !otpSent ? "otp-send-attention" : ""
            }`}
          >
            {otpSending ? (
              <span className="inline-flex items-center gap-2">
                <ArrowsClockwise size={14} weight="bold" className="animate-spin" /> Đang gửi OTP…
              </span>
            ) : otpSent ? (
              otpCooldown > 0 ? (
                <span data-testid="register-otp-cooldown">Gửi lại OTP sau {otpCooldown}s</span>
              ) : (
                "Gửi lại OTP"
              )
            ) : (
              "Gửi OTP Zalo & Tạo tài khoản"
            )}
          </button>

          {/* Helper text dưới nút nếu chưa đủ điều kiện */}
          {!otpSent && sendOtpDisabledReason && (
            <div
              className="mt-2 text-[11px] text-amber-300/85 leading-tight text-center"
              data-testid="register-send-otp-hint"
            >
              {sendOtpDisabledReason}
            </div>
          )}

          {/* OTP input — hiện sau khi gửi OTP */}
          {otpSent && (
            <div
              className="mt-3 p-3 rounded-xl bg-[#0A0C10] border border-[#FF8A00]/35"
              data-testid="register-otp-input-row"
            >
              <div className="text-[11.5px] text-white/75 mb-2 flex items-center gap-1.5">
                <ShieldCheck size={14} weight="bold" className="text-[#A8FF00]" />
                Nhập mã OTP 6 số gửi tới Zalo {normalizePhone(form.phone)} — tài khoản sẽ được tạo tự động ngay khi mã đúng.
              </div>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="• • • • • •"
                disabled={busy}
                className={`${inputClass} pl-3 tabular-nums tracking-[0.5em] text-center text-lg font-bold`}
                autoFocus
                data-testid="register-otp-input"
              />
              {busy && (
                <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-[#A8FF00]" data-testid="register-otp-submitting">
                  <ArrowsClockwise size={13} weight="bold" className="animate-spin" />
                  Đang tạo tài khoản…
                </div>
              )}
              {otpDebug && !busy && (
                <div className="mt-1.5 text-[10.5px] text-amber-300/85 font-mono text-center">
                  DEV mode: OTP = <strong>{otpDebug}</strong>
                </div>
              )}
            </div>
          )}

          {/* Phase 85.A.186 — Tạm ẩn đăng ký Google theo yêu cầu user.
              Khi cần bật lại, đổi `false &&` thành `!otpSent &&`. */}
          {false && !otpSent && (
            <>
              <div className="my-4 flex items-center gap-3">
                <div className="flex-1 h-px bg-white/8" />
                <span className="text-[10px] tracking-[0.25em] uppercase text-white/40 font-bold">Hoặc</span>
                <div className="flex-1 h-px bg-white/8" />
              </div>
              <GoogleSignInButton label="Đăng ký với Google" />
              <div className="mt-3">
                <AppleSignInButton label="Đăng ký với Apple" />
              </div>
            </>
          )}

          <div className="mt-5 text-center text-xs text-white/50">
            Đã có tài khoản?{" "}
            <Link to="/login" className="text-[#FF8A00] hover:text-[#FFB300] font-semibold">
              Đăng nhập
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputClass =
  "w-full bg-[#0A0C10] border border-white/15 focus:border-[#FF8A00] outline-none pl-10 pr-3 py-2.5 rounded-lg text-sm transition";

function Field({ icon: Icon, children, className = "" }) {
  return (
    <div className={`relative mb-3 ${className}`}>
      <Icon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 z-10" />
      {children}
    </div>
  );
}
